export const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.base.alfamall";

export const APP_STORE_URL =
  "https://apps.apple.com/us/app/alfamall/id6748519089";

export const GET_APP_LABEL = "Get AlfaMall App";
export const SCAN_TO_DOWNLOAD_LABEL = "Scan to download";
export const OPEN_APP_LABEL = "Open app";
export const CONTINUE_IN_BROWSER_LABEL = "Not now, continue in browser";
export const EXPLORE_IN_APP_LABEL = "Explore www.alfamall.com in...";
export const APP_NAME = "AlfaMall app";
export const APP_BANNER_TITLE = "Download the App now";
export const APP_BANNER_SUBTITLE = "Faster checkout & exclusive offers";
export const APP_BANNER_OPEN_LABEL = "Open";
export const DISMISS_STORAGE_KEY = "alfamallAppPromptDismissed";
export const BANNER_DISMISS_STORAGE_KEY = "alfamallAppBannerDismissed";
export const PROMO_POPUP_STORAGE_KEY = "promoPopup";
export const PROMO_POPUP_CLOSED_EVENT = "alfamall:promo-popup-closed";
const HIDDEN_APP_PROMPT_PATHS = [
  "/cart/checkout",
  "/login",
  "/password-protected",
  "/islamic-12-12",
  "/vertica",
];

export function isAppPromptHidden(pathname = "") {
  return HIDDEN_APP_PROMPT_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`),
  );
}

function isIosDevice() {
  const userAgent = navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(userAgent);
  const iPadOS =
    navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;

  return iOSDevice || iPadOS;
}

function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// The Alfa banking app embeds the site in an iframe inside its WebView.
// play.google.com refuses to load in an iframe, and at top level the Play
// page's Install button uses market:// / intent://, which the WebView cannot
// open ("Web page not available – net::ERR_UNKNOWN_URL_SCHEME").
function isInAppWebView() {
  if (typeof window === "undefined") return false;
  if (isInIframe()) return true;

  const userAgent = navigator.userAgent || "";
  const androidWebView =
    /Android/i.test(userAgent) &&
    (/; wv\)/i.test(userAgent) || /Version\/[\d.]+/i.test(userAgent));
  const iosWebView = isIosDevice() && !/Safari/i.test(userAgent);

  return (
    androidWebView ||
    iosWebView ||
    /webview|cordova|phonegap|react native/i.test(userAgent) ||
    window.ReactNativeWebView !== undefined ||
    typeof window.Android !== "undefined"
  );
}

const APP_SCHEME = "alfamall";
const APP_SCHEME_URL = `${APP_SCHEME}://`;
const STORE_FALLBACK_MS = 2500;

// If the app opens, the page is hidden and the store fallback is cancelled.
// If nothing handles appUrl (no app, no scheme, or desktop device mode), the
// page stays visible and the store opens instead.
function openAppWithStoreFallback(appUrl, storeUrl) {
  const openedAt = Date.now();

  const cleanup = () => {
    window.clearTimeout(timer);
    document.removeEventListener("visibilitychange", onHidden);
    window.removeEventListener("pagehide", cleanup);
  };

  const onHidden = () => {
    if (document.hidden) cleanup();
  };

  // Timers are throttled while the app is in front, so a late fire means the app opened.
  const timer = window.setTimeout(() => {
    cleanup();
    if (document.hidden) return;
    if (Date.now() - openedAt > STORE_FALLBACK_MS + 1000) return;
    window.location.href = storeUrl;
  }, STORE_FALLBACK_MS);

  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", cleanup);
  window.location.href = appUrl;
}

export const APP_DOWNLOAD_SHEET_EVENT = "alfamall:show-app-download";

export function getStoreUrl() {
  return isIosDevice() ? APP_STORE_URL : PLAY_STORE_URL;
}

export function getStoreName() {
  return isIosDevice() ? "App Store" : "Play Store";
}

export async function copyStoreLink() {
  const url = getStoreUrl();

  try {
    await navigator.clipboard.writeText(url);
    return true;
  } catch {
    // Cross-origin iframes usually lack the clipboard-write permission.
    const textarea = document.createElement("textarea");
    textarea.value = url;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(textarea);
    return copied;
  }
}

export function openApp() {
  // Navigating from the embedded view always ends on an error page, so stay
  // on the site and show download instructions instead.
  if (isInAppWebView()) {
    window.dispatchEvent(new Event(APP_DOWNLOAD_SHEET_EVENT));
    return;
  }

  // Custom schemes (alfamall://, intent://) only work on a real phone. On a
  // computer — including Chrome device mode and a local Docker build — they
  // log "scheme does not have a registered handler" and go nowhere.
  if (isDesktopBrowser()) {
    window.location.href = isIosDevice() ? APP_STORE_URL : PLAY_STORE_URL;
    return;
  }

  if (isIosDevice()) {
    openAppWithStoreFallback(APP_SCHEME_URL, APP_STORE_URL);
    return;
  }

  if (isAndroidBrowser()) {
    openAndroidApp();
    return;
  }

  window.location.href = PLAY_STORE_URL;
}

const ANDROID_PACKAGE = "com.base.alfamall";

function hasDesktopGpu() {
  try {
    const gl = document.createElement("canvas").getContext("webgl");
    const debugInfo = gl?.getExtension("WEBGL_debug_renderer_info");
    if (!debugInfo) return null;

    const renderer = String(
      gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || "",
    );
    if (/Adreno|Mali|PowerVR|Immortalis|Xclipse/i.test(renderer)) return false;
    if (/Apple M\d|Intel|NVIDIA|AMD|Radeon|GeForce|SwiftShader/i.test(renderer)) {
      return true;
    }
    return null;
  } catch {
    return null;
  }
}

// Chrome device mode spoofs the Android user agent, but the computer's
// platform (MacIntel / Win32) stays the same. Never use intent:// there.
function isDesktopBrowser() {
  const userAgent = navigator.userAgent || "";

  if (/Android/i.test(userAgent)) {
    // Chrome device mode spoofs an Android UA but keeps the computer GPU.
    return hasDesktopGpu() === true;
  }

  if (hasDesktopGpu() === true) return true;

  const platform = navigator.platform || "";
  if (/MacIntel|MacPPC|Win32|Win64|WOW64/i.test(platform)) return true;

  if (navigator.userAgentData?.mobile === false) return true;

  return false;
}

function isAndroidBrowser() {
  const userAgent = navigator.userAgent || "";
  if (!/Android/i.test(userAgent)) return false;
  if (isDesktopBrowser()) return false;

  const clientPlatform = navigator.userAgentData?.platform || "";
  if (clientPlatform && !/android/i.test(clientPlatform)) return false;

  return true;
}

function getAndroidHttpsIntentUrl() {
  const host = (window.location.hostname || "alfamall.com").replace(/^www\./, "");
  const path = window.location.pathname || "/";
  return `intent://${host}${path}#Intent;scheme=https;package=${ANDROID_PACKAGE};end`;
}

function getAndroidPackageSchemeIntentUrl() {
  return `intent://open#Intent;scheme=${ANDROID_PACKAGE};package=${ANDROID_PACKAGE};end`;
}

// Chrome will not launch MAIN/LAUNCHER from a web page. It will launch an
// ACTION_VIEW intent with a URI the app already handles. A web-wrapper app
// typically handles https://alfamall.com. Do not put Play Store in
// S.browser_fallback_url: Chrome treats "no matching filter" the same as
// "app not installed" and jumps to the store immediately.
function openAndroidApp() {
  const httpsIntent = getAndroidHttpsIntentUrl();
  const packageIntent = getAndroidPackageSchemeIntentUrl();
  const openedAt = Date.now();

  const stillOnPage = () =>
    !document.hidden && Date.now() - openedAt < STORE_FALLBACK_MS + 2500;

  const cleanup = () => {
    window.clearTimeout(secondTry);
    window.clearTimeout(storeFallback);
    document.removeEventListener("visibilitychange", onHidden);
    window.removeEventListener("pagehide", cleanup);
  };

  const onHidden = () => {
    if (document.hidden) cleanup();
  };

  document.addEventListener("visibilitychange", onHidden);
  window.addEventListener("pagehide", cleanup);

  window.location.href = httpsIntent;

  const secondTry = window.setTimeout(() => {
    if (!stillOnPage()) return;
    window.location.href = packageIntent;
  }, 700);

  const storeFallback = window.setTimeout(() => {
    cleanup();
    if (document.hidden) return;
    window.location.href = PLAY_STORE_URL;
  }, 3200);
}

export function isPromoPopupClosed() {
  return sessionStorage.getItem(PROMO_POPUP_STORAGE_KEY) === "true";
}

export function rememberPromoPopupClosed() {
  sessionStorage.setItem(PROMO_POPUP_STORAGE_KEY, "true");
  // sessionStorage writes don't fire "storage" in the same tab.
  window.dispatchEvent(new Event(PROMO_POPUP_CLOSED_EVENT));
}

export function rememberAppPromptDismissed() {
  sessionStorage.setItem(DISMISS_STORAGE_KEY, "1");
}

export function isAppPromptDismissed() {
  return sessionStorage.getItem(DISMISS_STORAGE_KEY) === "1";
}

export function rememberAppBannerDismissed() {
  sessionStorage.setItem(BANNER_DISMISS_STORAGE_KEY, "1");
}

export function isAppBannerDismissed() {
  return sessionStorage.getItem(BANNER_DISMISS_STORAGE_KEY) === "1";
}
